import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Wrap,
  Avatar,
  Input,
  Button,
  Text,
  Icon,
  Link,
  keyframes,
} from '@chakra-ui/react';
import { FiMessageSquare, FiSend, FiExternalLink, FiRefreshCw } from 'react-icons/fi';

import { renderMarkdownLite } from '../lib/markdownLite';

const DASHBOARD_EVENT_URL = (id) => `https://dashboard.selwynevents.com/event-planner/${id}`;

const EXAMPLES = [
  'What events do I have tomorrow?',
  'How many people have signed up for [event name]?',
  'Which events still have gaps in their rundown this week?',
];

const bounce = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
`;

const TypingDots = () => (
  <HStack spacing={1} px={4} py={3} bg="gray.100" borderRadius="lg" display="inline-flex">
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        as="span"
        w="6px"
        h="6px"
        borderRadius="full"
        bg="gray.500"
        animation={`${bounce} 1.2s ${i * 0.15}s infinite ease-in-out`}
      />
    ))}
  </HStack>
);

const Chat = ({ token, apiBase, tenantId, onSessionStarted, onNewChat }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const bottomRef = useRef(null);

  // Clears the local transcript/session and tells App.js to unlock the
  // instance picker (see its "Locked for this conversation" note) — the
  // only other way to get back to a pickable instance was a full page
  // refresh, which also threw away the transcript instead of just retiring it.
  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setInput('');
    onNewChat?.();
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Appends to the last message if it's the in-progress streaming assistant
  // reply, otherwise starts a new one — keeps the reducer logic in one place
  // for both real token chunks and locally-synthesized error text.
  const appendToAssistantReply = (delta) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.role === 'assistant' && last.streaming) {
        return [...prev.slice(0, -1), { ...last, content: last.content + delta }];
      }
      return [...prev, { role: 'assistant', content: delta, streaming: true }];
    });
  };

  const finishAssistantReply = () => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.role === 'assistant' && last.streaming) {
        return [...prev.slice(0, -1), { ...last, streaming: false }];
      }
      return prev;
    });
  };

  const attachReferences = (events) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.role === 'assistant') {
        return [...prev.slice(0, -1), { ...last, references: events }];
      }
      return prev;
    });
  };

  const send = async (text) => {
    if (!text.trim() || isLoading || !tenantId) return;
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setInput('');
    setIsLoading(true);
    try {
      const resp = await fetch(`${apiBase}/api/v1/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${token}`,
        },
        body: JSON.stringify({ session_id: sessionId, tenant_id: tenantId || undefined, message: text }),
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        appendToAssistantReply(`Error: ${data.error || 'request failed'}`);
        finishAssistantReply();
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let gotFirstToken = false;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // last entry may be a partial line — keep it for next read

        for (const line of lines) {
          if (!line.trim()) continue;
          const event = JSON.parse(line);
          if (event.type === 'session') {
            if (!sessionId) onSessionStarted?.();
            setSessionId(event.session_id);
          } else if (event.type === 'token') {
            if (!gotFirstToken) {
              gotFirstToken = true;
              setIsLoading(false); // stop the "waiting" state once real text starts arriving
            }
            appendToAssistantReply(event.text);
          } else if (event.type === 'references') {
            attachReferences(event.events);
          } else if (event.type === 'done') {
            finishAssistantReply();
          }
        }
      }
    } catch {
      appendToAssistantReply('Connection error — please try again.');
      finishAssistantReply();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex direction="column" height="100%" maxWidth="760px" mx="auto" width="100%">
      {messages.length > 0 && (
        <HStack justify="flex-end" px={4} pt={4}>
          <Button
            size="sm"
            variant="ghost"
            colorScheme="teal"
            leftIcon={<Icon as={FiRefreshCw} />}
            onClick={startNewChat}
            isDisabled={isLoading}
          >
            New Chat
          </Button>
        </HStack>
      )}
      <VStack flex="1" overflowY="auto" spacing={4} align="stretch" py={6} px={4}>
        {messages.length === 0 && (
          <Flex direction="column" align="center" justify="center" flex="1" color="gray.400" textAlign="center">
            <Flex
              w={16} h={16} borderRadius="full" bg="teal.50" align="center" justify="center" mb={4}
            >
              <Icon as={FiMessageSquare} boxSize={7} color="teal.400" />
            </Flex>
            <Text fontSize="sm" color="gray.500" mb={3}>
              {tenantId ? 'Try asking:' : 'Select an instance in the sidebar to get started.'}
            </Text>
            {tenantId && (
              <VStack spacing={1} align="stretch" maxWidth="420px">
                {EXAMPLES.map((ex) => (
                  <Button
                    key={ex}
                    variant="outline"
                    colorScheme="teal"
                    size="sm"
                    whiteSpace="normal"
                    height="auto"
                    py={2}
                    onClick={() => send(ex)}
                  >
                    {ex}
                  </Button>
                ))}
              </VStack>
            )}
          </Flex>
        )}
        {messages.map((m, i) => (
          <HStack key={i} align="flex-start" justify={m.role === 'user' ? 'flex-end' : 'flex-start'} spacing={2}>
            {m.role !== 'user' && (
              <Avatar size="sm" bg="teal.500" icon={<Icon as={FiMessageSquare} color="white" boxSize={4} />} />
            )}
            <VStack align={m.role === 'user' ? 'flex-end' : 'flex-start'} maxWidth="75%" spacing={1}>
              <Box
                bg={m.role === 'user' ? 'teal.500' : 'gray.100'}
                color={m.role === 'user' ? 'white' : 'gray.800'}
                px={4}
                py={2}
                borderRadius="lg"
                boxShadow="sm"
                whiteSpace="pre-wrap"
              >
                {m.role === 'user' ? m.content : renderMarkdownLite(m.content)}
                {m.streaming && <Box as="span" display="inline-block" w="2px" h="1em" bg="gray.500" ml="1px" verticalAlign="text-bottom" />}
              </Box>
              {m.references?.length > 0 && (
                <Wrap>
                  {m.references.map((ref) => (
                    <Link
                      key={ref.id}
                      href={DASHBOARD_EVENT_URL(ref.id)}
                      isExternal
                      fontSize="xs"
                      px={2}
                      py={1}
                      borderRadius="full"
                      bg="teal.50"
                      color="teal.700"
                      _hover={{ bg: 'teal.100', textDecoration: 'none' }}
                    >
                      {ref.name || `Event ${ref.id}`} <Icon as={FiExternalLink} boxSize={2.5} verticalAlign="middle" />
                    </Link>
                  ))}
                </Wrap>
              )}
            </VStack>
            {m.role === 'user' && <Avatar size="sm" bg="purple.500" />}
          </HStack>
        ))}
        {isLoading && !messages[messages.length - 1]?.streaming && (
          <HStack align="flex-start" spacing={2}>
            <Avatar size="sm" bg="teal.500" icon={<Icon as={FiMessageSquare} color="white" boxSize={4} />} />
            <TypingDots />
          </HStack>
        )}
        <div ref={bottomRef} />
      </VStack>
      <HStack p={4} borderTop="1px solid" borderColor="gray.200" bg="white">
        <Input
          value={input}
          placeholder={tenantId ? 'Ask about your events...' : 'Select an instance first...'}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send(input)}
          isDisabled={isLoading || !tenantId}
          borderRadius="full"
          bg="gray.50"
          focusBorderColor="teal.400"
        />
        <Button
          colorScheme="teal"
          borderRadius="full"
          onClick={() => send(input)}
          isLoading={isLoading}
          isDisabled={!tenantId}
          rightIcon={<Icon as={FiSend} />}
        >
          Send
        </Button>
      </HStack>
    </Flex>
  );
};

export default Chat;
